<template>
  <div data-cy="payment-added-modal" data-payment-added-modal>
    <div>
      <div>
        <PaymentAddedModal
          v-if="!isBusinessProfileComplete"
          title="Almost done!"
          :image="PictogramProfileIcon"
          :button-primary="{ label: 'Complete business profile' }"
          @primary-click="
            () => {
              $router.push({
                name: RouteNames.PROFILE.INDEX,
              });
              $emit('done');
            }
          "
        >
          <template #body>
            <p>For access to funding, please complete your business profile.</p>
          </template>
        </PaymentAddedModal>
        <PaymentAddedModal
          v-else-if="showConnectRevenueAccountModal"
          title="Your Application is Almost Complete!"
          :image="iconGlassGraphUp"
          image-alt="Icon showing a glass with an upward graph"
          :button-primary="{
            label: 'Connect a revenue account',
          }"
          @primary-click="handleConnectRevenueClick"
        >
          <template #body>
            <p>
              You've successfully submitted your request, but we need to verify
              your revenue before we review them. Once that’s done, we’ll start
              reviewing your application and keep you updated on every step —
              including approval and funding.
            </p>
          </template>
        </PaymentAddedModal>
        <PaymentAddedModal
          v-else-if="showUploadFinancialDocsModal"
          title="Additional Financial Documents Required"
          :image="PictogramListIcon"
          :button-primary="{
            label: 'Upload financial documents',
          }"
          :button-secondary="{ label: 'Skip for now' }"
          :show-default-help="true"
          @primary-click="
            () => {
              $router.push({
                name: RouteNames.FINANCIAL.DOCUMENTS,
              });
              $emit('done');
            }
          "
          @secondary-click="showFinancialDocsModal = false"
        >
          <template #body>
            <p>
              Your estimated Funding Capacity meets a threshold that requires
              the following financial documents: balance sheets and income/P&L
              statements of <strong>the most recent</strong> complete quarter
              and fiscal year.
            </p>
          </template>
        </PaymentAddedModal>
        <PaymentAddedModal
          v-else-if="showUpdatePaymentObligationsModal"
          title="Update Payment Obligations"
          :image="PictogramListIcon"
          :button-primary="{
            label: 'Update Payment Obligations',
          }"
          :button-secondary="{ label: 'Skip for now' }"
          :show-default-help="true"
          @primary-click="
            () => {
              $router.push({
                name: RouteNames.DEBT_OBLIGATIONS.INDEX,
              });
              $emit('done');
            }
          "
          @secondary-click="showDebtObligationsModal = false"
        >
          <template #body>
            <p>
              Please provide the latest information regarding your payment
              obligations to proceed with your funding application.
            </p>
          </template>
        </PaymentAddedModal>
        <PaymentAddedModal
          v-else
          :title="
            $t(
              'account.pages.pageVendors.paymentAdded.fundingApplicationSubmitted',
            )
          "
          :image="checkCircleIcon"
          image-alt="Green check mark icon"
          :button-primary="getRequestMoreCapitalLabel()"
          :button-secondary="{
            label: $t('account.pages.pageVendors.paymentAdded.done'),
          }"
          :left-align="true"
          :show-default-help="true"
          @primary-click="handleAddAnotherInvoiceClick"
          @secondary-click="handleDoneClick"
        >
          <template #body>
            <p>
              {{ capacityMessage }}
            </p>
          </template>
          <template #footer>
            <TimelineAccordion
              :header="
                $t(
                  'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.header',
                )
              "
              :data="fundingApplicationNextSteps"
              @accordion-expanded="handleAccordionExpanded"
            ></TimelineAccordion>
          </template>
        </PaymentAddedModal>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { RouteNames } from '@/data/routes';
import { mapGetters } from 'vuex';
import { SEGMENT_EVENTS } from '@/data/segment-events';
import analytics from '@/utils/analytics';
import PaymentAddedModal from '@/pages/payments/components/PaymentAddedModal.vue';
import TimelineAccordion from '../TimelineAccordion.vue';

import PictogramProfileIcon from '@/assets/icons/pictogram-profile.svg';
import PictogramListIcon from '@/assets/icons/pictogram-list.svg';
import iconGlassGraphUp from '@/assets/icons/icon-glass-graph-up.png';
import checkCircleIcon from '@/assets/icons/check-circle-icon.svg';

export default {
  components: {
    PaymentAddedModal,
    TimelineAccordion,
  },
  data() {
    return {
      RouteNames,
      showFinancialDocsModal: true,
      showDebtObligationsModal: true,
      showVerifyRevenueModal: true,
      PictogramProfileIcon: markRaw(PictogramProfileIcon),
      PictogramListIcon: markRaw(PictogramListIcon),
      iconGlassGraphUp: markRaw(iconGlassGraphUp),
      checkCircleIcon: markRaw(checkCircleIcon),
    };
  },
  async mounted() {
    await this.$store.dispatchApiAction('GET_FISCAL_DOCUMENT_CHECKS');

    if (this.showFundingApplicationSubmittedModal) {
      analytics.track(
        SEGMENT_EVENTS.if_funding_app_submission_modal_shown,
        this.commonAnalyticsProperties,
      );
    }
  },
  computed: {
    ...mapGetters([
      'isBusinessProfileComplete',
      'billFlowType',
      'isInvoiceFlow',
      'userCanSubmitInvoices',
      'isInFinancialDocsSegment',
      'fiscalDocumentsUpToDate',
      'salesAccountsConnected',
      'hasDisconnectedSalesAccounts',
      'shouldShowDebtObligationNotifications',
      'isManualUnderwriting',
      'business',
      'businessCapacity',
    ]),
    /** @public */
    showConnectRevenueAccountModal() {
      return (
        !this.isManualUnderwriting &&
        (!this.salesAccountsConnected || this.hasDisconnectedSalesAccounts) &&
        this.showVerifyRevenueModal
      );
    },
    showUploadFinancialDocsModal() {
      return (
        this.isInFinancialDocsSegment &&
        !this.fiscalDocumentsUpToDate &&
        this.showFinancialDocsModal
      );
    },
    showUpdatePaymentObligationsModal() {
      return (
        this.shouldShowDebtObligationNotifications &&
        this.showDebtObligationsModal
      );
    },
    showFundingApplicationSubmittedModal() {
      return (
        !this.showConnectRevenueAccountModal &&
        !this.showUploadFinancialDocsModal &&
        !this.showUpdatePaymentObligationsModal
      );
    },
    commonAnalyticsProperties() {
      return {
        businessId: this.business.id,
        businessName: this.business.name,
        billType: this.billFlowType,
      };
    },
    fundingApplicationNextSteps() {
      const applicationReview = {
        title: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.applicationReview.title',
        ),
        body: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.applicationReview.body',
        ),
      };
      const signContract = {
        title: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.signContract.title',
        ),
        body: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.signContract.body',
        ),
      };
      const transferFunds = {
        title: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.transferFunds.title',
        ),
        body: this.isInvoiceFlow
          ? this.$t(
              'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.transferFunds.invoiceBody',
            )
          : this.$t(
              'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.transferFunds.cashAdvanceBody',
            ),
      };
      const payClearco = {
        title: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.payClearco.title',
        ),
        body: this.$t(
          'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.payClearco.body',
        ),
      };
      if (this.isPrelimaryCapacity) {
        return [
          applicationReview,
          {
            title: this.$t(
              'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.signContract.title',
            ),
            body: this.$t(
              'account.pages.pageVendors.paymentAdded.fundingApplicationNextSteps.signContract.preliminaryCapacityBody',
            ),
          },
          transferFunds,
          payClearco,
        ];
      }

      return [signContract, transferFunds, payClearco];
    },
    isPrelimaryCapacity() {
      return this.businessCapacity && !this.businessCapacity.approvedAt;
    },
    capacityMessage() {
      return this.isPrelimaryCapacity
        ? this.$t('account.pages.pageVendors.paymentAdded.preliminaryCapacity')
        : this.$t('account.pages.pageVendors.paymentAdded.approvedCapacity');
    },
  },
  methods: {
    getRequestMoreCapitalLabel() {
      return this.userCanSubmitInvoices
        ? {
            label: this.$t(
              'account.pages.pageVendors.paymentAdded.requestMoreCapital',
            ),
          }
        : undefined;
    },
    handleAddAnotherInvoiceClick() {
      analytics.track(
        SEGMENT_EVENTS.if_funding_app_add_another_invoice_clicked,
        this.commonAnalyticsProperties,
      );
      this.$emit('addAnotherInvoice');
    },
    handleDoneClick() {
      analytics.track(
        SEGMENT_EVENTS.if_funding_app_done_clicked,
        this.commonAnalyticsProperties,
      );
      this.$emit('done');
    },
    handleAccordionExpanded() {
      analytics.track(
        SEGMENT_EVENTS.if_funding_app_whats_next_expanded,
        this.commonAnalyticsProperties,
      );
    },
    handleConnectRevenueClick() {
      analytics.track(SEGMENT_EVENTS.if_connect_revenue_account_clicked, {
        ...this.commonAnalyticsProperties,
        modalContext: 'bill_submission_flow',
      });
      this.$emit('verifyRevenue');
    },
  },
};
</script>

<style lang="less" module>
.bnpl-card {
  max-width: 526px;
  max-height: 204px;
  margin: 30px auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.bnpl-card-title {
  padding-bottom: 15px;
}
.icon-text-group {
  display: flex;
  align-items: center;
  margin: 2px 0;
}
.icon {
  height: 24px;
  width: 24px;
  margin-right: 5px;
}

button.button-add-invoice {
  color: @color-purple-2022 !important;
  margin-top: 30px;
}

button.button-done {
  padding: 0;
}
</style>
